/**
 * Penelope in the trade ticket (the Penelope tab of BotModeSwitch): the surface
 * every trader has. BASIC by default — the side switches, eight knobs, live or practice,
 * one Run button, and one sentence about what she is doing. ADVANCED (every
 * knob, presets, kill, the research panel) sits behind a disclosure that only a
 * member whose manager has not switched Advanced off can open. Live is the
 * default whenever the box allows it and the member is not locked to practice.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Scale } from 'lucide-react';
import { cn } from '@/lib/infra/utils';
import { downloadCsvResponse } from '@/lib/infra/csvExport';
import { apiFetch } from '@/stores/authStore';
import { useStreamriftMode } from '@/stores/streamriftMode';
import { penelopeAttachInput, type PenelopeConfig } from '@/hooks/usePenelope';
import { PENELOPE_SPORT_LABEL, penelopeSportOfEvent, usePenelopeSides, type PenelopeSport } from '@/hooks/usePenelopeSides';
import { usePenelopeAccess, usePenelopeAdvancedAccess } from '@/hooks/usePenelopeAccess';
import { usePenelopeProfile } from '@/hooks/usePenelopeProfile';
import type { GameRow } from '@/views/ValueDashboard/types';
import { PenelopeEditor } from '@/views/Penelope/PenelopeEditor';
import { PenelopeActionBar } from '@/views/Penelope/PenelopeActionBar';
import { knobBaseline } from '@/views/Penelope/baseline';
import { MiniGamePanel } from './MiniGamePanel';
import { PenelopeAnchorStrip } from './PenelopeAnchor';
import { PenelopeWhyFor } from './PenelopeWhy';
import { PenelopeFlagButton } from './PenelopeFlagButton';
import { PENELOPE_PRESETS, PENELOPE_START } from './penelopePresets';
import { penelopeStatusSentence } from './penelopeStatus';
import { PenelopeSideToggle } from './PenelopeSideToggle';

export { PENELOPE_START };

const signed = (n: number): string => (n > 0 ? `+${n}` : `${n}`);
/** R10: the game's timeline CSV (the relay streams it; a refusal is logged, never thrown into the ticket). */
async function downloadTimeline(id: string): Promise<void> {
  try {
    const res = await apiFetch(`/api/penelope/games/${id}/timeline`);
    if (!res.ok) throw new Error(((await res.json().catch(() => ({}))) as { error?: string }).error || `HTTP ${res.status}`);
    await downloadCsvResponse(res, `penelope-${id}-timeline.csv`);
  } catch (e) {
    console.warn('[penelope] timeline download failed', (e as Error).message);
  }
}
const LIVE_CONFIRM = 'Run Penelope LIVE on this game? She will place REAL post-only orders on Kalshi as you, against your limits.';
const GO_LIVE_CONFIRM = 'Switch Penelope to LIVE for this game? She will place REAL post-only orders on Kalshi with these settings. Her simulated orders are dropped and she starts again live.';
const PRACTICE_CONFIRM = 'Back to practice? Her resting Kalshi orders are cancelled and she simulates from here.';
const SAVE_LIVE_CONFIRM = 'Save this as your default with a LIVE start? From now on one click on a game row starts Penelope LIVE there — REAL post-only orders on Kalshi as you, both sides, these settings. Games already running keep their own settings.';

/** `miniInTicket` false: the page shows the mini game's panel elsewhere (Game Detail, beside the books), so the ticket
 *  shows one line about it instead of a second copy. */
export function PenelopeTicket({ game, miniInTicket = true }: { game: GameRow; miniInTicket?: boolean }) {
  const access = usePenelopeAccess();
  const advanced = usePenelopeAdvancedAccess();
  // StreamRift mode (2026-10-20): the settings map in the editor and one action row under the status. Off = today's ticket.
  const sr = useStreamriftMode();
  const sides = usePenelopeSides(game, access);
  const p = sides.control;
  const [draftState, setDraft] = useState<Partial<PenelopeConfig>>(PENELOPE_START);
  /** The trader's own pick; 'auto' follows what the box and their lock allow. */
  const [modeChoice, setModeChoice] = useState<'auto' | 'live' | 'shadow'>('auto');
  // Update 01 PEN-08: the saved default is the starting draft (until the trader edits it) and its start mode the pick.
  // 2026-10-14: the default this game starts from is its sport's own, else the all-sports one.
  const sport = penelopeSportOfEvent(penelopeAttachInput(game)?.eventTicker);
  const prof = usePenelopeProfile(access, sport);
  const saved = prof.data?.profile ?? null;
  const savedFor: 'all' | PenelopeSport = saved?.sport ?? 'all';
  const sportLabel = sport ? PENELOPE_SPORT_LABEL[sport] : null;
  const [touched, setTouched] = useState(false);
  useEffect(() => {
    if (!saved || touched) return;
    setDraft({ ...PENELOPE_START, ...saved.config });
    setModeChoice(saved.startMode);
  }, [saved, touched]);
  // PR-005 (Doom baseline 10-20): with no saved default the sizes start at the relay's own — Max Long 10,000, clamped to
  // this box's ceiling — so the draft shows what a Start would run.
  const boxDefaults = prof.data?.defaults ?? null;
  // Derived, not set in an effect: the box's sizes sit under the draft until a saved default replaces it, and are folded
  // into the draft on the trader's first edit.
  const withBoxSizes = (d: Partial<PenelopeConfig>): Partial<PenelopeConfig> => (saved || !boxDefaults ? d
    : { maxNetContracts: boxDefaults.maxNetContracts, leanMaxNetContracts: boxDefaults.leanMaxNetContracts, maxGrossContracts: boxDefaults.maxGrossContracts, ...d });
  const draft = touched ? draftState : withBoxSizes(draftState);
  const editDraft = (patch: Partial<PenelopeConfig>) => { setDraft((d) => ({ ...(touched ? d : withBoxSizes(d)), ...patch })); setTouched(true); };
  if (!access) return null;

  const row = p.game;
  const cfg = row?.config ?? null;
  const live = row?.live?.baseline ?? null;
  const isLive = cfg?.mode === 'live';
  const real = row?.live ? !row.live.shadow : false;
  const armed = cfg?.enabled === true;
  const draftMode: 'live' | 'shadow' = !p.liveEligible ? 'shadow' : modeChoice === 'auto' ? 'live' : modeChoice;
  const practiceOnly = p.liveEligible ? null : p.liveReason ?? 'live orders are not available here';
  const primary = 'w-full rounded py-1.5 text-sm font-bold disabled:opacity-50';
  const small = 'rounded border px-2 py-1 text-[10px] font-bold uppercase tracking-wide disabled:opacity-50';
  const anySide = sides.armed.A || sides.armed.B;
  // PEN-01: the two switches name the team she may BUY; they replace the lean and sit beside each book on Game Detail.
  const buys = (
    <div className="flex items-center gap-3 text-[11px]" data-testid="penelope-buys">
      <span className="text-muted-foreground">she may buy</span>
      <PenelopeSideToggle sides={sides} side="A" teamCode={game.awayCode} otherCode={game.homeCode} />
      <PenelopeSideToggle sides={sides} side="B" teamCode={game.homeCode} otherCode={game.awayCode} />
    </div>
  );
  const slots = !p.slots ? null
    : p.slots.mine >= p.slots.maxMine ? `you have ${p.slots.mine} of your ${p.slots.maxMine} games armed on this box — stop one to run another`
      : p.slots.maxArmed - p.slots.running <= 2 ? `${p.slots.running} of ${p.slots.maxArmed} game slots on this box in use` : null;

  /** PEN-08: save these settings as the caller's one default — what a one-click Start on a dashboard row runs (both
   *  sides, no lean). A LIVE start mode is confirmed here, once. Future launches only; nothing running changes. */
  const defaultBlock = (settings: Partial<PenelopeConfig>, startMode: 'live' | 'shadow') => {
    const saveAs = (forSport: 'all' | PenelopeSport, label: string) => (
      <button
        key={forSport}
        type="button"
        disabled={prof.busy || p.busy}
        onClick={() => {
          const live = startMode === 'live';
          if (live && !window.confirm(SAVE_LIVE_CONFIRM)) return;
          void prof.save(settings, startMode, live, forSport);
        }}
        className={cn(small, startMode === 'live' ? 'border-red-500 text-red-500' : 'border-sky-500 text-sky-500')}
        title={forSport === 'all'
          ? 'One click on a game row of any sport without its own default then starts Penelope with these settings, both sides, in this mode. Games already running keep their own settings.'
          : `One click on a ${label} game row then starts Penelope with these settings, both sides, in this mode; other sports keep using your all-sports default.`}
      >
        {prof.busy ? 'saving…' : forSport === 'all' ? `Save as My Default (${startMode === 'live' ? 'live' : 'practice'})` : `Save as My ${label} Default`}
      </button>
    );
    return (
    <div className="border-border/60 flex flex-wrap items-center gap-2 border-t pt-1 text-[10px]" data-testid="penelope-default">
      {saveAs('all', 'all sports')}
      {sport && sportLabel && saveAs(sport, sportLabel)}
      <span className="text-muted-foreground">
        {saved
          ? `Your ${savedFor === 'all' ? 'all-sports' : PENELOPE_SPORT_LABEL[savedFor]} default v${saved.version}${savedFor === 'all' && sportLabel ? ` (no ${sportLabel} default)` : ''}: starts ${saved.startMode === 'live' ? 'LIVE' : 'in practice'} · Contract Size ${saved.config.clipContracts ?? '—'} · Max Long ${saved.config.maxNetContracts ?? '—'}${prof.data?.invalid.length ? ' — no longer fits this box: save it again' : ''}`
          : 'No saved default yet: one click on a game row needs one.'}
      </span>
      {prof.error && <span role="alert" className="text-red-500">{prof.error}</span>}
    </div>
    );
  };

  // R9: the one editor's Advanced head — the desk's presets.
  const presets: ReactNode = (
    <div className="flex flex-wrap items-center gap-1">
      <span className="text-muted-foreground text-[10px]">preset</span>
      {PENELOPE_PRESETS.map((preset) => (
        <button
          key={preset.key}
          type="button"
          disabled={p.busy}
          onClick={() => (row ? void p.patch({ ...preset.config, mode: undefined } as Partial<PenelopeConfig>) : editDraft(preset.config))}
          className="border-border/60 text-muted-foreground hover:text-foreground rounded border px-1.5 py-px font-mono text-[10px]"
          title={preset.hint}
        >
          {preset.label}
        </button>
      ))}
    </div>
  );
  // What one-click Start would run on this game: the changed-from-default base.
  const base = knobBaseline(prof.data, sport);
  // The bar's mini game switch: its own row; with none yet it brings the panel into view (never attaches by itself).
  const mg = p.minigame;
  const mini = mg ? {
    exists: !!mg.game, armed: mg.game?.config.enabled === true, live: mg.game?.config.mode === 'live',
    setArm: (on: boolean) => void mg.setArm(on),
    open: () => {
      const panel = document.querySelector<HTMLElement>('[data-testid="minigame-panel"]');
      if (panel) { panel.scrollIntoView?.({ block: 'nearest' }); return; }
      document.querySelector<HTMLElement>('[data-testid="minigame-open"]')?.click();
    },
  } : null;

  return (
    <div className="space-y-3 p-3">
      <div className="flex items-center gap-2">
        <Scale className="text-muted-foreground h-4 w-4" />
        <div className="text-foreground text-sm font-semibold">
          Penelope · {game.awayCode} @ {game.homeCode}
        </div>
        {row && (
          <span className={cn('rounded border px-1.5 py-px font-mono text-[10px] font-bold uppercase', isLive ? 'border-red-500 bg-red-500/15 text-red-500' : 'border-sky-500 text-sky-500')}>
            {isLive ? 'live' : 'practice'}
          </span>
        )}
      </div>
      <div className="flex min-w-0 items-center gap-2">
        <PenelopeAnchorStrip game={game} row={row} className="min-w-0" />
        {/* R10 Flag: the moment, saved on the relay, and an evidence line copied (Alt+Shift+F while the ticket is open). */}
        <PenelopeFlagButton
          row={row} source="ticket" hotkey
          status={row && cfg ? penelopeStatusSentence({ armed, parked: row.parked, practice: !isLive, teamA: game.awayCode, teamB: game.homeCode, live }) : undefined}
        />
      </div>

      {!row && (
        <>
          <p className="text-muted-foreground text-[11px]">
            Buys the teams you tick — here or beside each team's book — at the best bid when Pinnacle says it is worth it, and sells back at the best bid. Never deeper. Runs under your account and your limits.
          </p>
          {p.lastStopped && (
            <p role="alert" className="rounded border border-red-500/60 bg-red-500/10 px-2 py-1 text-[11px] text-red-500">
              Stopped {new Date(p.lastStopped.at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              {p.lastStopped.mode === 'shadow' ? ' (practice)' : ''}: {p.lastStopped.stopReason ?? 'no reason recorded'}. Run again below.
            </p>
          )}
          {p.eligible ? (
            <PenelopeEditor
              cfg={draft as PenelopeConfig}
              editable={!p.busy}
              save={editDraft}
              teamA={game.awayCode}
              teamB={game.homeCode}
              advanced={advanced}
              bounds={prof.data?.bounds}
              fieldErrors={prof.fieldErrors}
              base={base}
              mini={mini}
              sr={sr}
              surface="ticket"
              mapDensity="select"
              advancedHead={presets}
              between={<>
              {buys}
              <div className="flex items-center gap-2 text-[11px]">
                <span className="text-muted-foreground">orders</span>
                <div role="radiogroup" aria-label="live or practice" className="flex overflow-hidden rounded border">
                  <button
                    type="button"
                    role="radio"
                    aria-checked={draftMode === 'live'}
                    disabled={p.busy || !p.liveEligible}
                    onClick={() => setModeChoice('live')}
                    className={cn('px-2 py-0.5 font-mono text-[10px] font-bold uppercase disabled:opacity-40', draftMode === 'live' ? 'bg-red-500/15 text-red-500' : 'text-muted-foreground')}
                  >
                    live
                  </button>
                  <button
                    type="button"
                    role="radio"
                    aria-checked={draftMode === 'shadow'}
                    disabled={p.busy}
                    onClick={() => setModeChoice('shadow')}
                    className={cn('px-2 py-0.5 font-mono text-[10px] font-bold uppercase', draftMode === 'shadow' ? 'bg-sky-500/15 text-sky-500' : 'text-muted-foreground')}
                  >
                    practice
                  </button>
                </div>
                {practiceOnly && <span className="text-muted-foreground text-[10px]">practice only: {practiceOnly}</span>}
              </div>
              <button
                type="button"
                disabled={p.busy || !anySide}
                title={anySide ? undefined : 'Tick the team she may buy first — here or beside its book'}
                onClick={() => {
                  if (draftMode === 'live' && !window.confirm(LIVE_CONFIRM)) return;
                  void p.run({ ...draft, mode: draftMode, armA: sides.armed.A, armB: sides.armed.B });
                }}
                className={cn(primary, draftMode === 'live' ? 'bg-red-600 text-white' : 'bg-primary text-primary-foreground')}
              >
                {p.busy ? <Loader2 className="mx-auto h-4 w-4 animate-spin" /> : draftMode === 'live' ? 'Run Penelope live on this game' : 'Run Penelope in practice'}
              </button>
              {!anySide && <p className="text-muted-foreground text-[10px]">Tick the team she may buy to run her.</p>}
              {defaultBlock(draft, draftMode)}
              {slots && <p className="text-[10px] text-amber-500">{slots}</p>}
              </>}
            />
          ) : (
            <p className="text-muted-foreground text-[11px]">
              {p.reasons.length > 0 ? `Not available on this game: ${p.reasons.join('; ')}` : p.error ? '' : 'Checking this game…'}
            </p>
          )}
          {p.eligible && !p.serviceRunning && (
            <p className="text-[11px] text-amber-500">The Penelope service is off on this box, so she will not run until it is turned on.</p>
          )}
        </>
      )}

      {row && cfg && (
        <>
          <p className={cn('text-[11px]', live?.regime === 'HOLD' || live?.regime === 'PULL' ? 'text-sky-500' : live?.regime === 'PAUSED' || row.parked ? 'text-amber-500' : 'text-foreground')} data-testid="penelope-status">
            {penelopeStatusSentence({ armed, parked: row.parked, practice: !isLive, teamA: game.awayCode, teamB: game.homeCode, live })}
          </p>
          {/* R10 "Why?" per team: every reason behind the sentence. */}
          {live && (
            <div className="-mt-2 flex gap-1">
              <PenelopeWhyFor row={row} team="A" teamA={game.awayCode} teamB={game.homeCode} label={`why ${game.awayCode}?`} />
              <PenelopeWhyFor row={row} team="B" teamA={game.awayCode} teamB={game.homeCode} label={`why ${game.homeCode}?`} />
            </div>
          )}
          {live && (
            <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 font-mono text-[11px]">
              <dt className="text-muted-foreground">held</dt>
              <dd>{live.inv.A.contracts} {game.awayCode} / {live.inv.B.contracts} {game.homeCode} · net {signed(live.net)}</dd>
              <dt className="text-muted-foreground">{real ? 'P&L' : 'P&L (practice)'}</dt>
              <dd>${(live.pnlCents / 100).toFixed(2)} · {row.fillsCount} fills · {live.places} placed · {live.cancels} pulled</dd>
              <dt className="text-muted-foreground">Pinnacle</dt>
              <dd>
                {game.awayCode} {typeof live.fairC.A === 'number' ? live.fairC.A.toFixed(1) : '—'} · {game.homeCode} {typeof live.fairC.B === 'number' ? live.fairC.B.toFixed(1) : '—'}
                <span className="text-muted-foreground"> ({live.pinnacleState ?? 'no coverage'}{live.fairBlock ? `, ${live.fairBlock}` : ''})</span>
              </dd>
            </dl>
          )}

          {sr ? (
          // StreamRift mode: the one action row, in the same slot on every Penelope screen (under the status, above
          // the settings). Kill keeps its Advanced gating; the timeline CSV and the desk link sit behind "more".
          <PenelopeActionBar
            busy={p.busy} armed={armed} isLive={isLive}
            arm={{ set: (on) => void p.setArm(on), liveStartLabel: true }}
            mode={{ canGoLive: p.liveEligible, reason: practiceOnly ? `practice only: ${practiceOnly}` : null, set: (m) => void p.patch({ mode: m }), labels: 'short', confirmBack: true, goLiveConfirm: GO_LIVE_CONFIRM }}
            kill={advanced ? () => void p.kill() : null}
            remove={() => void p.remove()}
            more={[
              ...(advanced ? [{ label: 'timeline CSV', onClick: () => void downloadTimeline(row.id), title: 'Download this game\'s timeline: every regime change, order placed and pulled (with her reason), fill and flag, in UTC and Pacific time, across every day it ran on this box' }] : []),
              { label: 'Penelope desk →', to: '/penelope', title: 'Every game on one screen, service status and the variant comparison' },
            ]}
          />
          ) : (
          <div className="flex flex-wrap items-center gap-1.5">
            <button
              type="button"
              disabled={p.busy}
              onClick={() => void p.setArm(!armed)}
              className={cn(small, armed ? 'border-border text-foreground' : isLive ? 'border-red-500 bg-red-500/15 text-red-500' : 'border-sky-500 bg-sky-500/15 text-sky-500')}
              title={armed ? 'Stop quoting on this game (cancels her orders, keeps her inventory)' : isLive ? 'Start: she will place REAL post-only orders on Kalshi' : 'Start in practice: simulated, never writes to Kalshi'}
            >
              {armed ? 'stop' : isLive ? 'start (live)' : 'start'}
            </button>
            <button
              type="button"
              disabled={p.busy || (!isLive && !p.liveEligible)}
              onClick={() => {
                if (isLive) { if (window.confirm(PRACTICE_CONFIRM)) void p.patch({ mode: 'shadow' }); }
                else if (window.confirm(GO_LIVE_CONFIRM)) void p.patch({ mode: 'live' });
              }}
              className={cn(small, isLive ? 'border-border text-muted-foreground hover:text-foreground' : 'border-red-500 text-red-500')}
              title={isLive ? 'Back to practice (asks first)' : p.liveEligible ? 'Go live (asks first)' : `practice only: ${practiceOnly}`}
            >
              {isLive ? 'practice' : 'go live'}
            </button>
            <span className="flex-1" />
            <button type="button" disabled={p.busy} onClick={() => { if (window.confirm('Remove Penelope from this game? Her resting orders are cancelled; her inventory stays yours.')) void p.remove(); }} className="text-muted-foreground hover:text-foreground text-[10px] uppercase" title="Remove Penelope from this game (asks first)">
              remove
            </button>
          </div>
          )}

          {buys}
          {armed && !anySide && <p className="text-[10px] text-amber-500">No team ticked: she buys nothing and only sells back what she holds.</p>}
          <PenelopeEditor
            cfg={cfg}
            editable
            busy={p.busy}
            save={(patch) => void p.patch(patch)}
            teamA={game.awayCode}
            teamB={game.homeCode}
            advanced={advanced}
            bounds={prof.data?.bounds}
            base={base}
            row={{ armed, setArm: (on) => void p.setArm(on) }}
            mini={mini}
            live={isLive}
            sr={sr}
            surface="ticket"
            mapDensity="select"
            between={<>
              <div className="text-muted-foreground text-[10px]">Settings save as you leave a field and take effect within a second.</div>
              {defaultBlock(cfg, isLive ? 'live' : 'shadow')}
            </>}
            advancedHead={presets}
            advancedFoot={sr ? (
              <p className="text-muted-foreground text-[10px]">kill and the timeline CSV are on the action row under the status, above the settings.</p>
            ) : (
            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={p.busy}
                onClick={() => { if (window.confirm('Kill Penelope on this game? This stops the game for good.')) void p.kill(); }}
                className={cn(small, 'border-red-500 text-red-500')}
                title="Stop this game for good"
              >
                kill
              </button>
              {/* R10 (PR-064): this game's timeline — every decision, order, fill and flag in UTC and Pacific time. */}
              <button
                type="button"
                disabled={p.busy}
                onClick={() => void downloadTimeline(row.id)}
                className={small}
                title="Download this game's timeline: every regime change, order placed and pulled (with her reason), fill and flag, in UTC and Pacific time, across every day it ran on this box"
              >
                timeline CSV
              </button>
              <Link to="/penelope" className="text-primary text-[11px] underline">
                Every game on one screen, service status and the variant comparison are on the Penelope desk →
              </Link>
            </div>
            )}
          />
        </>
      )}

      {p.error && <p className="text-destructive text-xs">{p.error}</p>}

      {/* Doom 10-17: the mini game's approved side panel — its own row, in Penelope's market sidebar. Errors show above.
          R9: where the page shows it beside the books (Game Detail), one line here instead of a second copy. */}
      {p.minigame && (p.eligible || p.minigame.game) && (miniInTicket
        ? <MiniGamePanel game={game} m={p.minigame} busy={p.busy} error={null} bounds={prof.data?.bounds} />
        : (
          <p className="text-muted-foreground border-border/60 border-t pt-1 text-[10px]" data-testid="minigame-line">
            mini game: {p.minigame.game ? `${p.minigame.game.config.enabled ? 'on' : 'off'} · ${p.minigame.game.config.mode === 'live' ? 'live' : 'practice'}` : 'not started'} — its panel is under the books on this page
          </p>
        ))}
    </div>
  );
}
